<template>
  <div class="pwa-home">
    <!-- 헤더 -->
    <div class="home-header">
      <div class="header-left">
        <span class="greeting">{{ getGreeting() }}</span>
        <span class="user-name">{{ userInfo?.m_name || userInfo?.m_user_name || '센터장' }}님</span>
      </div>
      <div class="header-right">
        <button class="year-chip" @click="showYearPicker = true">
          {{ selectedYear }}년
          <v-icon size="14">mdi-chevron-down</v-icon>
        </button>
      </div>
    </div>

    <!-- 센터 카드 -->
    <div class="center-card" @click="showCenterMenu = true">
      <div class="center-icon">
        <v-icon size="24" color="white">mdi-domain</v-icon>
      </div>
      <div class="center-info">
        <span class="center-name">{{ userInfo?.m_sisul_name || '센터' }}</span>
        <span class="center-status">
          <span class="status-dot"></span>
          운영중
        </span>
      </div>
      <v-icon size="20" color="#ccc">mdi-chevron-right</v-icon>
    </div>

    <!-- 공지 배너 -->
    <div v-if="latestNotice" class="notice-card" @click="goToNotice">
      <div class="notice-icon">
        <v-icon size="16" color="white">mdi-bullhorn</v-icon>
      </div>
      <span class="notice-text">{{ latestNotice.nc_title }}</span>
      <v-icon size="16" color="#999">mdi-chevron-right</v-icon>
    </div>

    <!-- 이번 달 요약 -->
    <div class="month-summary">
      <div class="summary-header">
        <span class="summary-title">{{ getLatestMonth() }}월 현황</span>
        <span class="summary-badge" :class="getMagamStatus()">{{ getMagamStatusText() }}</span>
      </div>
      <div class="summary-stats">
        <div class="stat-item">
          <span class="stat-value">{{ getLatestMonthData()?.count || 0 }}<span class="stat-unit">명</span></span>
          <span class="stat-label">급여 대상</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <span class="stat-value">{{ formatShortCurrency(getLatestMonthData()?.wage || 0) }}</span>
          <span class="stat-label">총 인건비</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <span class="stat-value">{{ formatShortCurrency(getLatestMonthData()?.profit || 0) }}</span>
          <span class="stat-label">센터 수익</span>
        </div>
      </div>
    </div>

    <!-- 임금관리 현황 -->
    <div class="section">
      <div class="section-header">
        <span class="section-title">임금관리</span>
        <button class="section-more" @click="$router.push('/pwa/payroll')">
          상세보기 <v-icon size="14">mdi-chevron-right</v-icon>
        </button>
      </div>
      <PwaWageProcessStatus :selected-year="selectedYear" />
    </div>

    <!-- 연간 통계 -->
    <div class="section">
      <div class="section-header">
        <span class="section-title">{{ selectedYear }}년 연간 통계</span>
      </div>
      <div class="annual-stats">
        <div class="annual-card">
          <div class="annual-icon blue">
            <v-icon size="20" color="white">mdi-cash-multiple</v-icon>
          </div>
          <div class="annual-info">
            <span class="annual-label">총 인건비</span>
            <span class="annual-value">{{ formatCurrency(getTotalWage()) }}원</span>
          </div>
        </div>
        <div class="annual-card">
          <div class="annual-icon green">
            <v-icon size="20" color="white">mdi-chart-line</v-icon>
          </div>
          <div class="annual-info">
            <span class="annual-label">센터 수익</span>
            <span class="annual-value">{{ formatCurrency(getTotalProfit()) }}원</span>
          </div>
        </div>
        <div class="annual-card">
          <div class="annual-icon purple">
            <v-icon size="20" color="white">mdi-account-group</v-icon>
          </div>
          <div class="annual-info">
            <span class="annual-label">평균 인원</span>
            <span class="annual-value">{{ getAverageEmployeeCount() }}명</span>
          </div>
        </div>
        <div class="annual-card">
          <div class="annual-icon orange">
            <v-icon size="20" color="white">mdi-piggy-bank</v-icon>
          </div>
          <div class="annual-info">
            <span class="annual-label">총 절감액</span>
            <span class="annual-value">{{ formatCurrency(getTotalSavings()) }}원</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 빠른 메뉴 -->
    <div class="section">
      <div class="section-header">
        <span class="section-title">빠른 메뉴</span>
      </div>
      <div class="quick-menu">
        <button class="quick-btn" @click="$router.push('/pwa/employees')">
          <div class="quick-icon"><v-icon size="22">mdi-account-group</v-icon></div>
          <span>직원관리</span>
        </button>
        <button class="quick-btn" @click="$router.push('/pwa/payroll')">
          <div class="quick-icon"><v-icon size="22">mdi-currency-krw</v-icon></div>
          <span>급여관리</span>
        </button>
        <button class="quick-btn" @click="$router.push('/pwa/finance')">
          <div class="quick-icon"><v-icon size="22">mdi-chart-bar</v-icon></div>
          <span>회계관리</span>
        </button>
        <button class="quick-btn" @click="$router.push('/pwa/report')">
          <div class="quick-icon"><v-icon size="22">mdi-file-chart</v-icon></div>
          <span>보고서</span>
        </button>
      </div>
    </div>

    <!-- 공지사항 -->
    <div class="section" v-if="notices.length > 0">
      <div class="section-header">
        <span class="section-title">공지사항</span>
        <button class="section-more" @click="$router.push('/pwa/notice')">
          더보기 <v-icon size="14">mdi-chevron-right</v-icon>
        </button>
      </div>
      <div class="notice-list">
        <div 
          v-for="notice in notices.slice(0, 3)" 
          :key="notice.nc_idx" 
          class="notice-item"
          @click="goToNoticeDetail(notice.nc_idx)"
        >
          <span class="notice-title">{{ notice.nc_title }}</span>
          <span class="notice-date">{{ formatNoticeDate(notice.nc_wdate) }}</span>
        </div>
      </div>
    </div>

    <!-- 센터 메뉴 -->
    <div class="sheet-overlay" :class="{ open: showCenterMenu }" @click="showCenterMenu = false"></div>
    <div class="sheet" :class="{ open: showCenterMenu }">
      <div class="sheet-handle"></div>
      <div class="sheet-header">
        <span class="sheet-title">메뉴</span>
        <button class="sheet-close" @click="showCenterMenu = false">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>
      <div class="sheet-content">
        <div class="menu-item" @click="goToUserInfo">
          <v-icon size="22" color="#1e3a8a">mdi-account-circle</v-icon>
          <span>내 정보</span>
          <v-icon size="18" color="#ccc">mdi-chevron-right</v-icon>
        </div>
        <div class="menu-item" @click="handleInquiry">
          <v-icon size="22" color="#1e3a8a">mdi-headset</v-icon>
          <span>고객센터</span>
          <span class="menu-sub">070-1234-5678</span>
        </div>
        <div class="menu-item logout" @click="handleLogout">
          <v-icon size="22" color="#ef4444">mdi-logout</v-icon>
          <span>로그아웃</span>
        </div>
      </div>
    </div>

    <!-- 연도 선택 -->
    <div class="sheet-overlay" :class="{ open: showYearPicker }" @click="showYearPicker = false"></div>
    <div class="sheet" :class="{ open: showYearPicker }">
      <div class="sheet-handle"></div>
      <div class="sheet-header">
        <span class="sheet-title">연도 선택</span>
        <button class="sheet-close" @click="showYearPicker = false">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>
      <div class="sheet-content">
        <div class="year-grid">
          <button
            v-for="year in availableYears"
            :key="year"
            class="year-btn"
            :class="{ active: selectedYear === year }"
            @click="selectYear(year)"
          >
            {{ year }}년
          </button>
        </div>
      </div>
    </div>

    <LodingComponent :is-visible="isLoading" :text="loadingText" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import axios from 'axios';
import LodingComponent from '@/components/loading/LodingComponent.vue';
import PwaWageProcessStatus from '@/components/pwa/PwaWageProcessStatus.vue';

const store = useStore();
const router = useRouter();

const userInfo = computed(() => store.state.user);
const isLoading = ref(false);
const loadingText = ref('로딩 중...');
const selectedYear = computed(() => store.state.pwa.selectedYear);
const showYearPicker = ref(false);
const showCenterMenu = ref(false);
const wageMagamData = ref<any[]>([]);
const latestNotice = ref<{ nc_idx: number; nc_title: string } | null>(null);
const notices = ref<any[]>([]);

const currentYear = new Date().getFullYear();
const availableYears = computed(() => 
  Array.from({ length: 6 }, (_, i) => currentYear - 3 + i)
);

// 인사말
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return '좋은 아침이에요,';
  if (hour < 18) return '안녕하세요,';
  return '좋은 저녁이에요,';
};

// 최신 마감 월
const getLatestMonth = () => {
  const latest = getLatestMagamMonth();
  return latest?.month || new Date().getMonth() + 1;
};

const getLatestMagamMonth = (): { month: number; yearMonth: string } | null => {
  if (!wageMagamData.value || wageMagamData.value.length === 0) return null;
  
  const totalData = wageMagamData.value.filter(item => item.wm_job_type === '전체합계');
  if (totalData.length === 0) return null;
  
  const sortedData = [...totalData].sort((a, b) => {
    const aYm = parseInt(a.wm_ym || '0');
    const bYm = parseInt(b.wm_ym || '0');
    return bYm - aYm;
  });
  
  if (sortedData.length === 0) return null;
  
  const latest = sortedData[0];
  const yearMonth = latest.wm_ym || '';
  const month = parseInt(yearMonth.substring(4, 6));
  
  return { month, yearMonth };
};

// 마감 상태
const getMagamStatus = () => {
  const latest = getLatestMagamMonth();
  if (!latest) return 'pending';
  
  const latestData = wageMagamData.value.find(
    item => item.wm_job_type === '전체합계' && item.wm_ym === latest.yearMonth
  );
  
  return latestData?.wm_isuse === 'y' ? 'closed' : 'progress';
};

const getMagamStatusText = () => {
  const status = getMagamStatus();
  if (status === 'closed') return '마감완료';
  if (status === 'progress') return '진행중';
  return '미시작';
};

// 최근 월 데이터
const getLatestMonthData = () => {
  const latest = getLatestMagamMonth();
  if (!latest) return null;
  
  const latestData = wageMagamData.value.find(
    item => item.wm_job_type === '전체합계' && item.wm_ym === latest.yearMonth
  );
  
  if (!latestData) return null;
  
  return {
    month: latest.month,
    wage: parseInt(latestData.wm_wage_sum) || 0,
    profit: parseInt(latestData.wm_center_profit) || 0,
    savings: parseInt(latestData.wm_center_savings) || 0,
    count: parseInt(latestData.wm_count) || 0
  };
};

// KPI 계산
const getTotalWage = (): number => {
  if (!wageMagamData.value || wageMagamData.value.length === 0) return 0;
  return wageMagamData.value
    .filter(item => item.wm_job_type === '전체합계')
    .reduce((sum, item) => sum + (parseInt(item.wm_wage_sum) || 0), 0);
};

const getTotalProfit = (): number => {
  if (!wageMagamData.value || wageMagamData.value.length === 0) return 0;
  return wageMagamData.value
    .filter(item => item.wm_job_type === '전체합계')
    .reduce((sum, item) => sum + (parseInt(item.wm_center_profit) || 0), 0);
};

const getAverageEmployeeCount = (): number => {
  if (!wageMagamData.value || wageMagamData.value.length === 0) return 0;
  const totalData = wageMagamData.value.filter(item => item.wm_job_type === '전체합계');
  if (totalData.length === 0) return 0;
  const totalCount = totalData.reduce((sum, item) => sum + (parseInt(item.wm_count) || 0), 0);
  return Math.round(totalCount / totalData.length);
};

const getTotalSavings = (): number => {
  if (!wageMagamData.value || wageMagamData.value.length === 0) return 0;
  return wageMagamData.value
    .filter(item => item.wm_job_type === '전체합계')
    .reduce((sum, item) => sum + (parseInt(item.wm_center_savings) || 0), 0);
};

// 포맷
const formatCurrency = (value: number): string => {
  return value.toLocaleString();
};

const formatShortCurrency = (value: number): string => {
  if (value >= 100000000) {
    return `${(value / 100000000).toFixed(1)}억`;
  }
  if (value >= 10000) {
    return `${Math.floor(value / 10000).toLocaleString()}만`;
  }
  return value.toLocaleString();
};

const formatNoticeDate = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return `${date.getMonth() + 1}.${date.getDate()}`;
};

// 데이터 조회
const fetchYearlyWageMagamData = async (year: number) => {
  try {
    const user = store.state.user;
    if (!user || !user.m_sisul_code) return;

    isLoading.value = true;
    loadingText.value = '데이터를 불러오는 중...';
    
    const response = await axios.get(`/api/calc/wage-magam-yearly/${user.m_sisul_code}/${year}`, {
      withCredentials: true
    });

    if (response.data.success) {
      wageMagamData.value = response.data.data || [];
    } else {
      wageMagamData.value = [];
    }
  } catch (error) {
    console.error('급여 마감 데이터 조회 오류:', error);
    wageMagamData.value = [];
  } finally {
    isLoading.value = false;
  }
};

const loadNotices = async () => {
  try {
    const response = await axios.get('/api/notice/list', {
      params: { page: 1, limit: 5 },
      withCredentials: true
    });
    
    if (response.data.success) {
      notices.value = response.data.notices || [];
      if (notices.value.length > 0) {
        latestNotice.value = notices.value[0];
      }
    }
  } catch (error) {
    console.error('공지사항 로드 오류:', error);
  }
};

// 네비게이션
const selectYear = async (year: number) => {
  store.commit('pwa/setPwaYear', year);
  showYearPicker.value = false;
  await fetchYearlyWageMagamData(year);
};

const goToNotice = () => router.push('/pwa/notice');
const goToNoticeDetail = (id: number) => router.push(`/pwa/notice/${id}`);
const goToUserInfo = () => {
  showCenterMenu.value = false;
  router.push('/pwa/userinfo');
};

const handleInquiry = () => {
  showCenterMenu.value = false;
  window.location.href = 'tel:070-1234-5678';
};

const handleLogout = async () => {
  showCenterMenu.value = false;
  try {
    await store.dispatch('pwa/performPwaLogout');
  } catch (error) {
    console.error('로그아웃 오류:', error);
    store.commit('pwa/clearPwaUser');
  }
  router.push('/pwa/login');
};

onMounted(async () => {
  const savedYear = localStorage.getItem('pwa_selectedYear');
  if (savedYear) {
    const year = parseInt(savedYear, 10);
    if (!isNaN(year) && year !== store.state.pwa.selectedYear) {
      store.commit('pwa/setPwaYear', year);
    }
  }
  
  const year = store.state.pwa.selectedYear;
  await Promise.all([
    fetchYearlyWageMagamData(year),
    loadNotices()
  ]);
});

watch(
  () => store.state.pwa.selectedYear,
  async (newYear) => {
    if (newYear) {
      await fetchYearlyWageMagamData(newYear);
    }
  }
);
</script>

<style scoped lang="scss">
@import '@/assets/styles/fonts.scss';

.pwa-home {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  font-family: $Noto_Sans_KR !important;
  * { font-family: $Noto_Sans_KR !important; }
}

// 헤더
.home-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 16px 16px;
  padding-top: calc(20px + env(safe-area-inset-top, 0px));
  background: #fff;

  .header-left {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .greeting { font-size: 13px; color: #888; }
    .user-name { font-size: 20px; font-weight: 700; color: #111; }
  }

  .year-chip {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 6px 10px;
    background: #f0f0f0;
    border: none;
    border-radius: 16px;
    font-size: 13px;
    font-weight: 600;
    color: #333;
  }
}

// 센터 카드
.center-card {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 12px 12px;
  padding: 14px;
  background: linear-gradient(135deg, #1e3a8a, #3b82f6);
  border-radius: 12px;
  cursor: pointer;

  .center-icon {
    width: 44px;
    height: 44px;
    background: rgba(255,255,255,0.2);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .center-info {
    flex: 1;

    .center-name {
      display: block;
      font-size: 16px;
      font-weight: 700;
      color: #fff;
    }

    .center-status {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: rgba(255,255,255,0.8);
      margin-top: 2px;

      .status-dot {
        width: 6px;
        height: 6px;
        background: #10b981;
        border-radius: 50%;
      }
    }
  }
}

// 공지 배너
.notice-card {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 12px 12px;
  padding: 12px;
  background: #fff;
  border-radius: 10px;
  cursor: pointer;

  &:active { background: #fafafa; }

  .notice-icon {
    width: 28px;
    height: 28px;
    background: #f59e0b;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .notice-text {
    flex: 1;
    font-size: 13px;
    font-weight: 500;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// 이번 달 요약
.month-summary {
  margin: 0 12px 16px;
  padding: 16px;
  background: #fff;
  border-radius: 12px;

  .summary-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;

    .summary-title {
      font-size: 15px;
      font-weight: 700;
      color: #111;
    }

    .summary-badge {
      padding: 4px 8px;
      border-radius: 10px;
      font-size: 11px;
      font-weight: 600;

      &.closed { background: #d1fae5; color: #047857; }
      &.progress { background: #fef3c7; color: #b45309; }
      &.pending { background: #f3f4f6; color: #6b7280; }
    }
  }

  .summary-stats {
    display: flex;
    align-items: center;

    .stat-item {
      flex: 1;
      text-align: center;

      .stat-value {
        display: block;
        font-size: 18px;
        font-weight: 700;
        color: #1e3a8a;

        .stat-unit { font-size: 13px; font-weight: 600; color: #6b7280; }
      }

      .stat-label {
        display: block;
        font-size: 11px;
        color: #888;
        margin-top: 2px;
      }
    }

    .stat-divider {
      width: 1px;
      height: 30px;
      background: #e5e5e5;
    }
  }
}

// 섹션
.section {
  margin: 0 12px 16px;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    padding: 0 4px;

    .section-title {
      font-size: 15px;
      font-weight: 700;
      color: #111;
    }

    .section-more {
      display: flex;
      align-items: center;
      gap: 2px;
      font-size: 12px;
      color: #888;
      background: none;
      border: none;
    }
  }
}

// 연간 통계
.annual-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.annual-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: #fff;
  border-radius: 10px;

  .annual-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;

    &.blue { background: linear-gradient(135deg, #1e3a8a, #3b82f6); }
    &.green { background: linear-gradient(135deg, #059669, #10b981); }
    &.purple { background: linear-gradient(135deg, #7c3aed, #a78bfa); }
    &.orange { background: linear-gradient(135deg, #ea580c, #f97316); }
  }

  .annual-info {
    flex: 1;
    min-width: 0;

    .annual-label {
      display: block;
      font-size: 11px;
      color: #888;
    }

    .annual-value {
      display: block;
      font-size: 14px;
      font-weight: 700;
      color: #111;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

// 빠른 메뉴
.quick-menu {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;

  .quick-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 14px 8px;
    background: #fff;
    border: none;
    border-radius: 10px;
    cursor: pointer;

    &:active { background: #fafafa; }

    .quick-icon {
      width: 40px;
      height: 40px;
      background: #f0f4ff;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #1e3a8a;
    }

    span {
      font-size: 11px;
      font-weight: 600;
      color: #333;
    }
  }
}

// 공지 리스트
.notice-list {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;

  .notice-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 14px;
    border-bottom: 1px solid #f5f5f5;
    cursor: pointer;

    &:last-child { border-bottom: none; }
    &:active { background: #fafafa; }

    .notice-title {
      flex: 1;
      font-size: 13px;
      color: #333;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .notice-date {
      font-size: 12px;
      color: #999;
      margin-left: 10px;
    }
  }
}

// 바텀시트
.sheet-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 200;
  opacity: 0;
  visibility: hidden;
  transition: all 0.25s;
  &.open { opacity: 1; visibility: visible; }
}

.sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 16px 16px 0 0;
  z-index: 201;
  transform: translateY(100%);
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
  max-height: 70vh;
  overflow: hidden;

  &.open { transform: translateY(0); }

  .sheet-handle {
    width: 36px;
    height: 4px;
    background: #ddd;
    border-radius: 2px;
    margin: 8px auto;
  }

  .sheet-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;

    .sheet-title { font-size: 17px; font-weight: 700; }
    .sheet-close {
      padding: 4px;
      background: #f5f5f5;
      border-radius: 50%;
      border: none;
    }
  }

  .sheet-content {
    padding: 16px;
    max-height: calc(70vh - 60px);
    overflow-y: auto;
  }
}

// 메뉴 아이템
.menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;

  &:last-child { border-bottom: none; }

  span:not(.menu-sub) {
    flex: 1;
    font-size: 15px;
    font-weight: 500;
    color: #333;
  }

  .menu-sub {
    font-size: 13px;
    color: #1e3a8a;
    font-weight: 500;
  }

  &.logout span { color: #ef4444; }
}

// 연도 그리드
.year-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  .year-btn {
    padding: 14px;
    background: #f5f5f5;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    color: #333;

    &.active {
      background: #1e3a8a;
      color: #fff;
      font-weight: 600;
    }
  }
}
</style>
